import { Outlet, NavLink } from 'react-router-dom';
import { Building2, DollarSign, ListChecks, LogOut, Users } from 'lucide-react';
import { BrandLogo } from '@components/brand/BrandLogo';
import { useAuthStore } from '@stores/authStore';
import { ThemeToggle } from '@components/ThemeToggle';

const platformNavItems = [
  { path: '/platform/tenants', label: 'Tenants', icon: Building2 },
  { path: '/platform/plan-requests', label: 'Plan Requests', icon: ListChecks },
  { path: '/platform/pricing', label: 'Pricing', icon: DollarSign },
];

// Main Super Admin only (the backend enforces it too).
const adminManagementNavItem = { path: '/platform/admin-management', label: 'Admin Management', icon: Users };

export default function PlatformLayout() {
  const { user, logout } = useAuthStore();
  const navItems = user?.isPrimary ? [...platformNavItems, adminManagementNavItem] : platformNavItems;

  const handleLogout = async () => {
    await logout();
    // Full document load rather than client-side navigation, so no in-memory
    // store survives into the next sign-in in this tab. Same reasoning as
    // MainLayout's handleLogout.
    window.location.assign('/platform-admin');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Bar */}
      <header className="bg-indigo-700 dark:bg-indigo-900 text-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              {/* The bar is indigo in both modes, so the white logo always. */}
              <BrandLogo height={22} tone="white" />
              <div className="border-l border-white/25 pl-3">
                <h1 className="text-base font-semibold leading-tight">Platform</h1>
                <p className="text-xs text-indigo-200">Administration Console</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <ThemeToggle />
              <div className="text-sm text-indigo-200">
                <NavLink
                  to="/platform/profile"
                  className="hover:text-white hover:underline transition-colors"
                  title="Account settings"
                >
                  {user?.email}
                </NavLink>
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-600 text-indigo-100">
                  {user?.isPrimary ? 'MAIN SUPER ADMIN' : user?.platformRole}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-200 hover:text-white hover:bg-indigo-600 rounded transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar Nav */}
          <nav className="w-56 flex-shrink-0">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
